import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormikErrors, useField, useFormikContext } from 'formik'
import { useNewTranslation } from 'hooks'
import qs from 'query-string'
import _ from 'lodash'
import { TemplateContentContainer } from '../style'
import Text from 'particles/Text'
import { FormikDatePicker } from 'particles/DatePicker'
import ReminderAlert from 'particles/Alert/ReminderAlert'
import styled from 'particles/themes/styled'
import RectangleButton from 'particles/Button/RectangleButton'
import DateInputRange from 'particles/RangeInput/DateInputRange'
import { parseBackendDate, convertToBackendStr, parseJsDate, getTimeZonedDateNow, addDay } from 'services/dates'
import { TemplateDetailSchema } from './index'
import {
    selectTemplateDetail,
    selectCampaignSchedule,
    updateCampaignSchedule,
    CampaignTemplateStep,
    getCampaignSchedule,
    selectCampaignInfo,
} from 'state/campaignTemplates'
import { selectCampaignByTemplateData, selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import { getScheduleSubtextTranslation } from 'components/utils/translationsMap'
import OnFormikValueChange, { OnFormikValueChangeProps } from '../OnFormikValueChange'
import Divider from 'components/Shared/Divider'
import Loading from '../Loading'
import { fieldIdentifiers } from 'components/Shared/Comment'
import TemplateInputFieldContainer from 'components/Shared/Permission/TemplateInputFieldContainer'
import EditTemplateRestricted from 'components/Shared/Permission/EditTemplateRestricted'
import { Country } from 'services/api/constants'
import { useTranslation } from 'react-i18next'
import { selectIsInternalUser } from 'state/profile'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import SaveChanges from '../SaveChanges'

interface DatePickerProps extends React.ComponentProps<typeof FormikDatePicker> {
    country: Country
    isReadOnly?: boolean
    showError: boolean
    allowEditForFutureDateOnly?: boolean
}

const DatePicker: React.FC<DatePickerProps> = ({
    name,
    isReadOnly,
    country,
    showError,
    label,
    subtext,
    identifier,
}) => {
    const [t] = useTranslation()
    const [, meta] = useField(name)
    const showDatePastError =
        meta.error === t('errorMessage.businessLogic.cantEnterPastDate') ||
        meta.error === t('errorMessage.businessLogic.payoutBeforeEndDate') ||
        showError

    return (
        <FormikDatePicker
            name={name}
            size={'m'}
            country={country}
            label={label}
            subtext={subtext}
            showError={showDatePastError}
            validateByTouch={false}
            isRequired={true}
            width={'224px'}
            placeholder="DD/MM/YYYY"
            labelSize="l"
            identifier={identifier}
            isReadOnly={isReadOnly}
        />
    )
}

interface CampaignScheduleProps {
    toNextSection: (isCompleted: boolean, isError: boolean) => void
    backPreviousSection: () => void
    showError: boolean
    isReadOnly?: boolean
    validateSpecificField: OnFormikValueChangeProps['validateSpecificField']
    updateCurrentStepStatus: (isCompleted: boolean, isError: boolean) => void
    setNavigationDisable?: React.Dispatch<React.SetStateAction<boolean>>
    isNavigationDisable?: boolean
    openWarningClickToast?: () => void
}

const CampaignSchedule: React.FC<CampaignScheduleProps> = ({
    toNextSection,
    backPreviousSection,
    showError: baseShowError,
    validateSpecificField,
    updateCurrentStepStatus,
    setNavigationDisable,
    isNavigationDisable,
    openWarningClickToast,
    isReadOnly,
}) => {
    const dispatch = useDispatch()
    const [t] = useNewTranslation()
    const { values } = useFormikContext<TemplateDetailSchema>()
    const params = qs.parse(location.search)
    const { templateData: templateDataUuid } = params
    const [, metaStartTime] = useField('start_time')
    const [, , helperPayoutDeadline] = useField('payout_deadline')
    const [prevStartTime, setPrevStartTime] = useState(metaStartTime.initialValue)
    const isLoading = useSelector(
        selectRequestsAreLoading([STATE_KEYS.UpdateCampaignSchedule, STATE_KEYS.GetCampaignSchedule]),
    )
    const campaignInfo = useSelector(selectCampaignInfo)
    const campaignSchedule = useSelector(selectCampaignSchedule)
    const templateDetail = useSelector(selectTemplateDetail)
    const campaign = useSelector(selectCampaignByTemplateData) // To control showError for Live campaign
    const [showError, setShowError] = useState(baseShowError)
    const taskTypeKeys = templateDetail?.key.split(':') ?? []
    const currentValueDate = new Date(parseJsDate(metaStartTime.initialValue, false))
    const currentDate = templateDetail ? getTimeZonedDateNow(templateDetail?.country) : new Date()
    const isStartTimeInTheFuture = metaStartTime.initialValue && currentDate < currentValueDate
    const showDatePastError = metaStartTime.error === t('errorMessage.businessLogic.cantEnterPastDate') || showError
    // As the backend defaults to returning null for all schedule fields,
    // we can determine whether to display detailed schedule data based on the campaign_time_window.
    const isScheduleReady = campaignSchedule?.campaign_time_window != null
    const isRegistrationEnabled =
        campaignInfo?.registration_enable && campaignSchedule?.registration_time_window != null
    // TODO to be remove in the future, this should be checked by permission
    const isInternalUser = useSelector(selectIsInternalUser)
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE

    const validateFieldsKey: (keyof TemplateDetailSchema)[] = []

    // Don't validate start time if campaign is live and current start time is not in the future
    if (!isCampaignLive || isStartTimeInTheFuture) {
        validateFieldsKey.push('start_time')
    }

    if (isInternalUser) {
        validateFieldsKey.push('payout_deadline')
    }
    const disableAutoSave = () => {
        // Enable data capture and preview update if campaign is live
        if (isCampaignLive || !templateDataUuid) {
            return false
        }

        if (campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT) {
            return false
        }

        return isReadOnly
    }

    useEffect(() => {
        if (templateDataUuid && typeof templateDataUuid === 'string') {
            dispatch(
                getCampaignSchedule({
                    templateDataUuid: templateDataUuid,
                }),
            )
        }
    }, [dispatch, templateDataUuid])

    useEffect(() => {
        // Update payout deadline if backend returns a new value
        campaignSchedule?.payout_deadline &&
            helperPayoutDeadline.setValue(
                parseBackendDate(campaignSchedule?.payout_deadline, templateDetail?.country, {
                    showTime: false,
                    showYear: true,
                    addSeconds: -1,
                }),
            )
    }, [campaignSchedule?.payout_deadline])

    useEffect(() => {
        setShowError(baseShowError)
    }, [baseShowError])

    const onNextClick = () => {
        if (isNavigationDisable) {
            openWarningClickToast?.()
            return
        }
        validateSpecificField(validateFieldsKey).then((errors: FormikErrors<TemplateDetailSchema>) => {
            const noError = Object.keys(errors).length === 0
            if (noError) {
                toNextSection(true, false)
            } else {
                if (showDatePastError) {
                    toNextSection(false, true)
                } else {
                    toNextSection(false, false)
                }
            }
        })
    }

    const onBackClick = () => {
        if (isNavigationDisable) {
            openWarningClickToast?.()
            return
        }

        backPreviousSection()
    }

    const onSaveData = (fieldsValues: Partial<TemplateDetailSchema>, showSuccessToast: boolean) => {
        const validTemplateDataUuid = typeof templateDataUuid === 'string' ? templateDataUuid : ''

        if (templateDetail?.country) {
            if (!_.isEmpty(fieldsValues)) {
                // Avoid calling the API if `fieldsValues` is empty, which typically indicates a validation error due to a past date. API calls should only be made once the user resolves this issue.
                const startTime = fieldsValues['start_time']
                    ? convertToBackendStr(fieldsValues['start_time'], templateDetail?.country)
                    : isCampaignLive && metaStartTime.initialValue // if campaign is live don't sent start_time as null
                      ? metaStartTime.initialValue
                      : null

                dispatch(
                    updateCampaignSchedule({
                        templateDataUuid: validTemplateDataUuid,
                        start_time: startTime,
                        payout_deadline: fieldsValues['payout_deadline']
                            ? convertToBackendStr(addDay(fieldsValues['payout_deadline'], 1), templateDetail?.country)
                            : null,
                        setNavigationDisable,
                        showSuccessToast,
                        // Only load if startTime was updated
                        skipLoading: prevStartTime === fieldsValues['start_time'],
                    }),
                )
                setPrevStartTime(fieldsValues['start_time'])
            }
        }
    }

    const handleDebouncedChange = useCallback(
        (fieldsValues: Partial<TemplateDetailSchema>) => {
            const validTemplateDataUuid = typeof templateDataUuid === 'string' ? templateDataUuid : ''
            // Retrieve data from backend before saving
            if (isCampaignLive) {
                if (
                    isStartTimeInTheFuture &&
                    validTemplateDataUuid &&
                    templateDetail &&
                    // only call getCampaignSchedule if start time is updated
                    prevStartTime !== fieldsValues['start_time']
                ) {
                    dispatch(
                        getCampaignSchedule({
                            templateDataUuid: validTemplateDataUuid as string,
                            start_time: fieldsValues['start_time']
                                ? convertToBackendStr(fieldsValues['start_time'], templateDetail?.country)
                                : undefined,
                        }),
                    )
                    setPrevStartTime(fieldsValues['start_time'])
                }
            } else {
                onSaveData(fieldsValues, false)
            }
        },
        [isCampaignLive, prevStartTime, isStartTimeInTheFuture],
    )

    const onSaveLiveCampaign = (onSuccessCallback: VoidFunction) => {
        validateSpecificField(validateFieldsKey).then(errors => {
            const noError = Object.keys(errors).length === 0
            if (noError) {
                const fieldsValues = _.pick(values, validateFieldsKey)
                const validFieldsValues = _.omitBy(fieldsValues, _.isNull)

                onSaveData(validFieldsValues, true)
                onSuccessCallback()
            } else {
                setShowError(true)
            }
        })
    }

    return (
        <>
            <CampaignScheduleContainer data-testid="test-campaign-schedule-page">
                <OnFormikValueChange
                    callback={handleDebouncedChange}
                    fields={validateFieldsKey}
                    skipCallbackError={{ start_time: [t('errorMessage.businessLogic.cantEnterPastDate')] }}
                    validateSpecificField={validateSpecificField}
                    showError={showError}
                    updateCurrentStepStatus={updateCurrentStepStatus}
                    // Only disableNavigation for input if campaign is not live
                    {...(!isCampaignLive && { setDisableNavigation: setNavigationDisable })}
                    disableAutoSave={disableAutoSave()}
                />
                <div className="form-title">
                    <Text scale="H4" color="primary">
                        {t('campaign.template.step2.campaignSchedule.title')}
                    </Text>
                </div>
                {templateDetail?.country && (
                    <TemplateInputFieldContainer checkForLiveEditBriefAndDeadline launchFieldEditable>
                        {(isReadOnly: boolean) => (
                            <DatePicker
                                name="start_time"
                                label={t('campaign.template.campaignSchedule.title')}
                                subtext={t('campaign.template.campaignSchedule.subText')}
                                country={templateDetail?.country}
                                showError={showError}
                                allowEditForFutureDateOnly
                                identifier={`${CampaignTemplateStep.CAMPAIGN_SCHEDULE}_${fieldIdentifiers.templateStartDate}`}
                                isReadOnly={isReadOnly || (isCampaignLive && !isStartTimeInTheFuture)}
                            />
                        )}
                    </TemplateInputFieldContainer>
                )}

                <Divider className="schedule-divider" />
                <ReminderAlert
                    form={'info'}
                    title={t('campaign.template.campaignSchedule.reminder.title')}
                    subtext={t('campaign.template.campaignSchedule.reminder.subText')}
                    isClosable={false}
                    width={'100%'}
                />
                {isLoading && <Loading title={' '} />}
                {!isLoading && isScheduleReady && (
                    <div className="date-range-container" data-testid="test-campaign-duration-block">
                        <Text scale="H3" color="primary">
                            {t('campaign.template.campaignDuration.title')}
                        </Text>

                        <DateInputRange
                            country={templateDetail?.country}
                            label={t('campaign.template.campaignDuration.startEndDate.title')}
                            subtext={t('campaign.template.campaignDuration.startEndDate.subText')}
                            firstRangeDate={
                                campaignSchedule?.campaign_time_window
                                    ? parseBackendDate(
                                          campaignSchedule?.campaign_time_window.start,
                                          templateDetail?.country,
                                      )
                                    : undefined
                            }
                            secondRangeDate={
                                campaignSchedule?.campaign_time_window
                                    ? parseBackendDate(
                                          campaignSchedule?.campaign_time_window.end,
                                          templateDetail?.country,
                                          {
                                              showTime: false,
                                              showYear: true,
                                              addSeconds: -1,
                                          },
                                      )
                                    : undefined
                            }
                            isReadOnly
                        />
                    </div>
                )}
                {!isLoading && isRegistrationEnabled && (
                    <div className="date-range-container" data-testid="test-registration-duration-block">
                        <Text scale="H3" color="primary">
                            {t('campaign.template.interestRegistration.title')}
                        </Text>

                        <DateInputRange
                            country={templateDetail?.country}
                            label={t('campaign.template.interestRegistration.startEndDate.title')}
                            subtext={t('campaign.template.interestRegistration.startEndDate.subText')}
                            firstRangeDate={
                                campaignSchedule?.registration_time_window
                                    ? parseBackendDate(
                                          campaignSchedule?.registration_time_window.start,
                                          templateDetail?.country,
                                      )
                                    : undefined
                            }
                            secondRangeDate={
                                campaignSchedule?.registration_time_window
                                    ? parseBackendDate(
                                          campaignSchedule?.registration_time_window.end,
                                          templateDetail?.country,
                                          {
                                              showTime: false,
                                              showYear: true,
                                              addSeconds: -1,
                                          },
                                      )
                                    : undefined
                            }
                            isReadOnly
                        />
                    </div>
                )}

                {!isLoading &&
                    isScheduleReady &&
                    campaignSchedule.tasks.map((task, taskIndex) => {
                        return (
                            <div key={task.title + taskIndex} className="date-range-container">
                                <Text scale="H3" color="primary">
                                    {task.title}
                                </Text>
                                {task.steps.map((step, stepIndex) => {
                                    const copyKey = taskTypeKeys[taskIndex] + '_' + stepIndex
                                    return (
                                        <DateInputRange
                                            key={step.title + stepIndex}
                                            country={templateDetail?.country}
                                            label={'Step ' + String(stepIndex + 1) + ': ' + step.title}
                                            subtext={getScheduleSubtextTranslation[copyKey]?.(t)}
                                            firstRangeDate={parseBackendDate(
                                                step.time_window.start,
                                                templateDetail?.country,
                                            )}
                                            secondRangeDate={parseBackendDate(
                                                step.time_window.end,
                                                templateDetail?.country,
                                                {
                                                    addSeconds: -1,
                                                },
                                            )}
                                            isReadOnly
                                        />
                                    )
                                })}
                            </div>
                        )
                    })}
                {templateDetail?.country && isInternalUser && (
                    <>
                        <Divider className="schedule-divider" />
                        <TemplateInputFieldContainer checkForLiveEditBriefAndDeadline launchFieldEditable>
                            <DatePicker
                                name="payout_deadline"
                                label={t('campaignEditor.completeSetup.enterPayoutDeadlineHeader')}
                                subtext={t('template.campaignSchedule.enterPayoutDeadlineSubtext')}
                                country={templateDetail?.country}
                                showError={showError}
                                allowEditForFutureDateOnly
                                identifier={`${CampaignTemplateStep.CAMPAIGN_SCHEDULE}_${fieldIdentifiers.templatePayoutDeadline}`}
                            />
                        </TemplateInputFieldContainer>
                    </>
                )}

                <EditTemplateRestricted>
                    <div className="button-group">
                        <div className="button">
                            <RectangleButton
                                type={'button'}
                                form={'outlined'}
                                size={'l'}
                                label={t('button.back')}
                                onClick={onBackClick}
                            />
                        </div>
                        <div className="button">
                            <RectangleButton
                                type={'button'}
                                size={'l'}
                                label={t('button.next')}
                                onClick={onNextClick}
                            />
                        </div>
                    </div>
                </EditTemplateRestricted>
            </CampaignScheduleContainer>
            <SaveChanges onSave={onSaveLiveCampaign} checkForLiveEditBriefAndDeadline />
        </>
    )
}

const CampaignScheduleContainer = styled(TemplateContentContainer)`
    margin-bottom: 24px;
    @media screen and (max-width: 768px) {
        margin-bottom: 0px;
    }

    .date-range-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        @media screen and (max-width: 768px) {
            max-width: 770px;
            width: fit-content;
        }

        border-radius: 12px;
        padding: 24px;
        background: ${({ theme }): string => theme.colors.surface.infoPastel};
        gap: ${({ theme }): string => theme.spacing[300]};
        .title-area {
            background: ${({ theme }): string => theme.colors.surface.infoPastel};
        }
    }
    .schedule-divider {
        color: ${({ theme }): string => theme.colors.divider.primary};
    }
    .button-group {
        display: flex;
        gap: ${({ theme }): string => theme.spacing[600]};
    }
`

export default CampaignSchedule
